import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AssistantPrompt as AssistantPromptUi } from '@triedge/ai-assistant-ui';
import type {
  DomainRouterMutationArgs,
  DomainRouterMutationData,
  DomainRouterQueryArgs,
  DomainRouterQueryData,
} from '../../api/domainRouterClient';
import { readDomainRouterErrorMessage } from '../../state/domainRouterError';
import type { AssistantRuntimePolicyHint } from '../../config/ai-assistant/execution-policy';
import {
  listAssistantPromptMatrixSnapshotForContext,
  type AssistantDataStateSignal,
} from '../../config/ai-assistant/prompt-matrix-snapshot';
import type { AssistantCapabilityCatalogScope } from '../../config/ai-assistant/capability-matrix';
import {
  routeMatchesAnyAssistantPattern,
  stripAssistantRoutePatternQualifier,
} from '../../config/ai-assistant/route-matching';
import {
  DEFAULT_ASSISTANT_TOOLBAR_PREFERENCES,
  parseAssistantToolToolbarPreferences,
  serializeAssistantToolToolbarPreferences,
  shouldApplyAssistantToolToolbarHydration,
  writeAssistantToolToolbarPreferences,
} from './assistantToolToolbarPreferences';
import { sanitizeAwsJsonObject } from './awsJsonSanitizer';
import type { AssistantSettingsSpecialistSnapshot } from './assistantSettingsSpecialist';
import type {
  AssistantActionPreviewResponse,
  AssistantCapability,
  AssistantTool,
  AdmissionsAIContext,
} from './types';
import type { AssistantCapabilityBrowserPreviewState } from './assistantCapabilityBrowser';
import {
  resolveAssistantCatalogFixtureSeed,
  resolveAuthorizedAssistantCatalogFailure,
  resolveAuthorizedAssistantCatalogSuccess,
} from './assistantCatalogAuthority';

type QueryTrigger<Name extends string, Args, Data> = (
  operation: Name,
  args: Args,
  preferCacheValue?: boolean,
) => Promise<Data>;

type MutationTrigger<Args, Data> = (args: Args) => Promise<Data>;

type AssistantCapabilityPromptControllerInput = {
  pathname: string;
  capabilityModule: NonNullable<AdmissionsAIContext['module']>;
  effectivePersonaRole: NonNullable<AdmissionsAIContext['personaRole']>;
  catalogScope?: AssistantCapabilityCatalogScope;
  context: AdmissionsAIContext;
  preferencesData: unknown;
  localAssistantCapabilityFixture: AssistantCapability[];
  localAssistantToolFixture: AssistantTool[];
  useLocalCatalogFixture?: boolean;
  settingsSpecialistSnapshot: AssistantSettingsSpecialistSnapshot | null;
  showAmbientSettingsSpecialist: boolean;
  triggerListAssistantCapabilities: QueryTrigger<
    'listAssistantCapabilities',
    DomainRouterQueryArgs<'listAssistantCapabilities'>,
    DomainRouterQueryData<'listAssistantCapabilities'>
  >;
  triggerListAssistantTools: QueryTrigger<
    'listAssistantTools',
    DomainRouterQueryArgs<'listAssistantTools'>,
    DomainRouterQueryData<'listAssistantTools'>
  >;
  triggerGetAssistantRuntimePolicy: QueryTrigger<
    'getAssistantRuntimePolicy',
    DomainRouterQueryArgs<'getAssistantRuntimePolicy'>,
    DomainRouterQueryData<'getAssistantRuntimePolicy'>
  >;
  triggerUpdateUserPreferences: MutationTrigger<
    DomainRouterMutationArgs<'updateUserPreferences'>,
    DomainRouterMutationData<'updateUserPreferences'>
  >;
  triggerPreviewAssistantAction: QueryTrigger<
    'previewAssistantAction',
    DomainRouterQueryArgs<'previewAssistantAction'>,
    DomainRouterQueryData<'previewAssistantAction'>
  >;
  onSetLastActionFeedback: (message: string | null) => void;
};

const toRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
);

const dedupe = <T,>(values: T[]): T[] => Array.from(new Set(values));

const normalizeAssistantText = (value: string): string =>
  value.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ');

const ASSISTANT_MODULE_LABELS: Record<AssistantCapability['module'], string> = {
  admissions: 'Admissions',
  'financial-aid': 'Financial Aid',
  faculty: 'Faculty',
  settings: 'Settings',
  'students-families': 'Students & Families',
  tuition: 'Tuition',
  attendance: 'Attendance',
  'parent-portal': 'Parent Portal',
  general: 'Operations',
};

const resolveScenarioTabFromPath = (pathname: string): string | null => {
  const path = pathname.toLowerCase();
  if (path.startsWith('/super-administrator/control-plane/attendance')) return 'Control Plane';
  if (path.startsWith('/super-administrator/control-plane')) return 'Control Plane';
  if (path === '/school/attendance') return 'Overview';
  if (path.startsWith('/school/attendance/new-records')) return 'New Records';
  if (path.startsWith('/school/attendance/teacher-view')) return 'Teacher View';
  if (path.startsWith('/school/attendance/students')) return 'Student History';
  if (path.startsWith('/school/settings/general')) return 'General';
  if (path.startsWith('/school/settings/users')) return 'Users';
  if (path.startsWith('/school/settings/admissions')) return 'Admissions';
  if (path.startsWith('/school/settings/financialaid') || path.startsWith('/school/settings/financial-aid')) {
    return 'Financial Aid';
  }
  if (path === '/school/students' || path.startsWith('/school/students/dashboard')) return 'Dashboard';
  if (path.startsWith('/school/students/family/')) {
    if (path.endsWith('/family')) return 'Family';
    if (path.endsWith('/tuition')) return 'Tuition';
    if (path.endsWith('/documents')) return 'Documents';
    if (path.endsWith('/notes')) return 'Notes';
    return 'Children';
  }
  if (path === '/school/faculty' || path === '/school/faculty/directory') return 'Directory';
  if (path.includes('/faculty/') && path.endsWith('/teaching-assignment')) return 'Teaching';
  if (path.includes('/faculty/profile/') && path.endsWith('/teaching')) return 'Teaching';
  if (path.endsWith('/faculty/profile/teaching')) return 'Teaching';
  if (path.includes('/faculty/') && path.endsWith('/notes')) return 'Notes';
  if (path.includes('/faculty/') && path.endsWith('/communications')) return 'Communications';
  if (path.startsWith('/school/faculty/')) return 'Profile';
  if (path === '/school/admissions' || path.startsWith('/school/admissions/dashboard')) return 'Dashboard';
  if (
    path.startsWith('/school/admissions/children/')
    || path.startsWith('/school/admissions/family/')
    || path.startsWith('/school/admissions/documents/')
    || path.startsWith('/school/admissions/notes/')
    || path.startsWith('/school/admissions/tuition/')
  ) {
    return 'Family Detail';
  }
  if (
    path === '/school/financialaid'
    || path === '/school/financialaid/staff'
    || path.startsWith('/school/financialaid/dashboard')
    || path === '/school/financial-aid'
    || path === '/school/financial-aid/staff'
    || path.startsWith('/school/financial-aid/dashboard')
  ) {
    return 'Dashboard';
  }
  if (path.startsWith('/school/financialaid/staff/') || path.startsWith('/school/financial-aid/staff/')) {
    return 'Application Detail';
  }
  if (path === '/school/academics/summary') return 'Summary';
  if (path === '/school/academics/approvals') return 'Approvals';
  if (path === '/school/substitutes') return 'Dashboard';
  if (path === '/school/substitutes/payroll') return 'Payroll';
  if (path === '/school/finance') return 'Overview';
  if (path === '/school/finance/reconciliation') return 'Reconciliation';
  if (path === '/school/payroll') return 'Dashboard';
  if (path.startsWith('/school/payroll/employee/')) return 'Employee Detail';
  if (path === '/school/camps') return 'Dashboard';
  if (path.startsWith('/school/camps/') && path !== '/school/camps/new') return 'Program Detail';
  if (path === '/school/donations') return 'Dashboard';
  if (path === '/school/donations/ledger') return 'Ledger';
  if (path === '/school/tip-a-teacher') return 'Overview';
  if (path.startsWith('/school/tip-a-teacher/settings')) return 'Settings';
  if (path.startsWith('/school/tip-a-teacher/notifications')) return 'Notifications';
  if (path === '/school/tuition') return 'Accounts';
  if (path === '/administrator/tuition') return 'Operations';
  if (path.startsWith('/parent/portal') || path.startsWith('/portal/family')) return 'Dashboard';
  return null;
};

const isSchoolFinancialAidPath = (path: string): boolean =>
  path.startsWith('/school/financialaid') || path.startsWith('/school/financial-aid');

const derivePromptDataStateSignals = (input: {
  pathname: string;
  context: AdmissionsAIContext;
}): AssistantDataStateSignal[] => {
  const path = input.pathname.toLowerCase();
  const signals: AssistantDataStateSignal[] = [];

  if (path.startsWith('/school/settings/users')) signals.push('pending_invites');
  if (path.includes('/documents')) signals.push('missing_documents');
  if (path.includes('/notes')) signals.push('overdue_commitments');
  if (isSchoolFinancialAidPath(path)) {
    signals.push('budget_pressure', 'committee_readiness');
  }

  const statusTokens = (input.context.status ?? []).map((status) => status.toLowerCase());
  if (statusTokens.some((status) => status.includes('wait') || status.includes('pending') || status.includes('in_progress'))) {
    signals.push('stalled_pipeline');
  }
  if (input.context.search?.trim()) signals.push('stalled_pipeline');
  if ((input.context.selectedIds?.length ?? 0) > 0 || Boolean(input.context.activeApplicationId)) {
    signals.push('committee_readiness');
  }

  return dedupe(signals.length > 0 ? signals : ['baseline']);
};

const toStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0) : [];

const routePrefixesMatchPathname = (pathname: string, routePrefixes: string[]): boolean =>
  routePrefixes.length > 0 && routeMatchesAnyAssistantPattern(pathname, routePrefixes);

const resolveAssistantCatalogTargetPathname = (input: {
  targetRoute?: string | null;
  routePrefixes?: string[];
  fallbackPathname: string;
}): string => {
  const candidate =
    stripAssistantRoutePatternQualifier(input.targetRoute ?? null) ||
    stripAssistantRoutePatternQualifier(input.routePrefixes?.[0] ?? null);
  return candidate || input.fallbackPathname;
};

const coerceAssistantModule = (value: unknown): AssistantCapability['module'] => {
  if (
    value === 'admissions'
    || value === 'financial-aid'
    || value === 'faculty'
    || value === 'settings'
    || value === 'students-families'
    || value === 'tuition'
    || value === 'attendance'
    || value === 'parent-portal'
    || value === 'general'
  ) {
    return value;
  }
  return 'general';
};

const coerceAssistantCapabilities = (payload: unknown): AssistantCapability[] => {
  const root = (payload as { listAssistantCapabilities?: unknown })?.listAssistantCapabilities ?? payload;
  if (!root || typeof root !== 'object') {
    return [];
  }
  const items = (root as { items?: unknown }).items;
  if (!Array.isArray(items)) {
    return [];
  }
  return items
    .map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }
      const item = entry as Record<string, unknown>;
      if (typeof item.id !== 'string' || item.id.length === 0) {
        return null;
      }
      return {
        id: item.id,
        page: typeof item.page === 'string' ? item.page : '',
        tab: typeof item.tab === 'string' ? item.tab : '',
        sequence: typeof item.sequence === 'number' && Number.isFinite(item.sequence) ? item.sequence : 0,
        title: typeof item.title === 'string' ? item.title : item.id,
        type: item.type === 'action' ? 'action' : 'insight',
        module: coerceAssistantModule(item.module),
        prompt: typeof item.prompt === 'string' ? item.prompt : '',
        rationale: typeof item.rationale === 'string' ? item.rationale : '',
        method: typeof item.method === 'string' ? item.method : null,
        requiresConfirmation: item.requiresConfirmation === true,
        actionKey: typeof item.actionKey === 'string' ? item.actionKey : null,
        targetRoute: typeof item.targetRoute === 'string' ? item.targetRoute : null,
        sourceOrigin: typeof item.sourceOrigin === 'string' ? item.sourceOrigin : null,
        scenarioKey: typeof item.scenarioKey === 'string' ? item.scenarioKey : null,
        likelySources: toStringArray(item.likelySources),
        routePrefixes: toStringArray(item.routePrefixes),
        dataStateSignals: toStringArray(item.dataStateSignals),
      } satisfies AssistantCapability;
    })
    .filter((entry): entry is AssistantCapability => entry !== null);
};

const coerceAssistantTools = (payload: unknown): AssistantTool[] => {
  const root = (payload as { listAssistantTools?: unknown })?.listAssistantTools ?? payload;
  if (!root || typeof root !== 'object') {
    return [];
  }
  const items = (root as { items?: unknown }).items;
  if (!Array.isArray(items)) {
    return [];
  }
  return items
    .map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }
      const item = entry as Record<string, unknown>;
      if (typeof item.toolId !== 'string' || item.toolId.length === 0) {
        return null;
      }
      const toolKind = item.toolKind;
      const executionPlane = item.executionPlane;
      const visibility = item.visibility;
      const promotion = item.promotion;
      const capabilityType = item.capabilityType;
      const taskClass = item.taskClass;
      const groupLabel = item.groupLabel;
      if (
        toolKind !== 'provider.native'
        && toolKind !== 'provider.bedrock'
        && toolKind !== 'custom.augmented'
        && toolKind !== 'custom.codified'
        && toolKind !== 'custom.interpreter'
      ) {
        return null;
      }
      if (
        executionPlane !== 'supervisor_query'
        && executionPlane !== 'ui_action'
        && executionPlane !== 'async_task'
      ) {
        return null;
      }
      if (visibility !== 'visible' && visibility !== 'hidden') {
        return null;
      }
      if (promotion !== 'promoted' && promotion !== 'available' && promotion !== 'hidden') {
        return null;
      }
      if (capabilityType != null && capabilityType !== 'insight' && capabilityType !== 'action') {
        return null;
      }
      if (
        taskClass != null
        && taskClass !== 'artifact_generate'
        && taskClass !== 'compute_report'
        && taskClass !== 'draft_then_approve'
        && taskClass !== 'external_side_effect'
      ) {
        return null;
      }
      if (
        groupLabel !== 'Insights'
        && groupLabel !== 'Actions'
        && groupLabel !== 'Custom Tools'
        && groupLabel !== 'Internal'
      ) {
        return null;
      }
      return {
        toolId: item.toolId,
        title: typeof item.title === 'string' ? item.title : item.toolId,
        summary: typeof item.summary === 'string' ? item.summary : '',
        toolKind,
        executionPlane,
        module: coerceAssistantModule(item.module),
        routePrefixes: toStringArray(item.routePrefixes),
        personas: toStringArray(item.personas),
        visibility,
        promotion,
        prompt: typeof item.prompt === 'string' ? item.prompt : null,
        requiresConfirmation: item.requiresConfirmation === true,
        executorRef: typeof item.executorRef === 'string' ? item.executorRef : null,
        providerToolName: typeof item.providerToolName === 'string' ? item.providerToolName : null,
        capabilityId: typeof item.capabilityId === 'string' ? item.capabilityId : null,
        capabilityType: capabilityType ?? null,
        actionKey: typeof item.actionKey === 'string' ? item.actionKey : null,
        workflowKey: typeof item.workflowKey === 'string' ? item.workflowKey : null,
        taskClass: taskClass ?? null,
        page: typeof item.page === 'string' ? item.page : null,
        tab: typeof item.tab === 'string' ? item.tab : null,
        sequence: typeof item.sequence === 'number' && Number.isFinite(item.sequence) ? item.sequence : null,
        method: typeof item.method === 'string' ? item.method : null,
        targetRoute: typeof item.targetRoute === 'string' ? item.targetRoute : null,
        sourceOrigin: typeof item.sourceOrigin === 'string' ? item.sourceOrigin : null,
        scenarioKey: typeof item.scenarioKey === 'string' ? item.scenarioKey : null,
        likelySources: toStringArray(item.likelySources),
        dataStateSignals: toStringArray(item.dataStateSignals),
        groupLabel,
        supportsProactivePromotion: item.supportsProactivePromotion === true,
        userVisible: item.userVisible === true,
      } satisfies AssistantTool;
    })
    .filter((entry): entry is AssistantTool => entry !== null);
};

const filterRouteScopedCapabilities = (
  capabilities: AssistantCapability[],
  input: {
    pathname: string;
    capabilityModule: NonNullable<AdmissionsAIContext['module']>;
  },
): AssistantCapability[] => (
  capabilities.filter((capability) => (
    capability.module === input.capabilityModule
    && routePrefixesMatchPathname(input.pathname, capability.routePrefixes)
  ))
);

const filterAssistantCapabilitiesForScope = (
  capabilities: AssistantCapability[],
  input: {
    pathname: string;
    capabilityModule: NonNullable<AdmissionsAIContext['module']>;
    catalogScope: AssistantCapabilityCatalogScope;
  },
): AssistantCapability[] => {
  if (input.catalogScope !== 'staff-global') {
    return filterRouteScopedCapabilities(capabilities, {
      pathname: input.pathname,
      capabilityModule: input.capabilityModule,
    });
  }
  return capabilities.filter((capability) => capability.module !== 'parent-portal');
};

const filterRouteScopedTools = (
  tools: AssistantTool[],
  input: {
    pathname: string;
    capabilityModule: NonNullable<AdmissionsAIContext['module']>;
    effectivePersonaRole: NonNullable<AdmissionsAIContext['personaRole']>;
  },
): AssistantTool[] => (
  tools.filter((tool) => (
    tool.module === input.capabilityModule
    && routePrefixesMatchPathname(input.pathname, tool.routePrefixes)
    && (
      tool.personas.length === 0
      || tool.personas.includes(input.effectivePersonaRole)
    )
  ))
);

const filterAssistantToolsForScope = (
  tools: AssistantTool[],
  input: {
    pathname: string;
    capabilityModule: NonNullable<AdmissionsAIContext['module']>;
    effectivePersonaRole: NonNullable<AdmissionsAIContext['personaRole']>;
    catalogScope: AssistantCapabilityCatalogScope;
  },
): AssistantTool[] => {
  const personaFiltered = tools.filter((tool) => (
    tool.personas.length === 0
    || tool.personas.includes(input.effectivePersonaRole)
  ));
  if (input.catalogScope !== 'staff-global') {
    return filterRouteScopedTools(personaFiltered, {
      pathname: input.pathname,
      capabilityModule: input.capabilityModule,
      effectivePersonaRole: input.effectivePersonaRole,
    });
  }
  return personaFiltered.filter((tool) => tool.module !== 'parent-portal');
};

const readPreferenceFilters = (value: unknown): Record<string, unknown> => {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as unknown;
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed as Record<string, unknown>
        : {};
    } catch {
      return {};
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
};

const coerceAssistantRuntimePolicyHint = (payload: unknown): AssistantRuntimePolicyHint | null => {
  const root = (payload as { getAssistantRuntimePolicy?: unknown })?.getAssistantRuntimePolicy ?? payload;
  if (!root || typeof root !== 'object') {
    return null;
  }
  const candidate = root as {
    executionMode?: unknown;
    revision?: unknown;
    policyJson?: unknown;
  };

  let policyJson: Record<string, unknown> | null = null;
  if (typeof candidate.policyJson === 'string' && candidate.policyJson.trim().length > 0) {
    try {
      const parsed = JSON.parse(candidate.policyJson);
      policyJson = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    } catch {
      policyJson = null;
    }
  }

  const multimodalGrounding = (() => {
    const node = policyJson?.multimodalGrounding;
    if (!node || typeof node !== 'object') {
      return undefined;
    }
    const value = node as Record<string, unknown>;
    return {
      visualExtractorModel:
        typeof value.visualExtractorModel === 'string' ? value.visualExtractorModel : null,
      teacherModel:
        typeof value.teacherModel === 'string' ? value.teacherModel : null,
    };
  })();

  const executionMode = candidate.executionMode === 'shadow' || candidate.executionMode === 'live'
    ? candidate.executionMode
    : undefined;
  const revision = typeof candidate.revision === 'number' && Number.isFinite(candidate.revision)
    ? candidate.revision
    : undefined;
  const lane = typeof policyJson?.lane === 'string' ? policyJson.lane : null;

  return {
    executionMode,
    revision,
    lane,
    multimodalGrounding,
  };
};

const coerceAssistantActionPreview = (payload: unknown): AssistantActionPreviewResponse => {
  const root = (payload as { previewAssistantAction?: unknown })?.previewAssistantAction ?? payload;
  if (!root || typeof root !== 'object') {
    throw new Error('previewAssistantAction returned an invalid payload.');
  }
  return root as AssistantActionPreviewResponse;
};

export const useAssistantCapabilityPromptController = (
  input: AssistantCapabilityPromptControllerInput,
) => {
  const [assistantCapabilities, setAssistantCapabilities] = useState<AssistantCapability[]>([]);
  const [assistantCapabilitiesLoading, setAssistantCapabilitiesLoading] = useState(false);
  const [assistantTools, setAssistantTools] = useState<AssistantTool[]>([]);
  const [assistantToolsLoading, setAssistantToolsLoading] = useState(false);
  const [assistantCatalogLoadErrors, setAssistantCatalogLoadErrors] = useState<{
    capabilities: string | null;
    tools: string | null;
  }>({
    capabilities: null,
    tools: null,
  });
  const [toolToolbarPreferences, setToolToolbarPreferences] = useState(DEFAULT_ASSISTANT_TOOLBAR_PREFERENCES);
  const [showCapabilityCatalog, setShowCapabilityCatalog] = useState(false);
  const [capabilityCatalogQuery, setCapabilityCatalogQuery] = useState('');
  const [capabilityBrowserPreviewById, setCapabilityBrowserPreviewById] = useState<Record<string, AssistantCapabilityBrowserPreviewState>>({});
  const [runtimePolicyHint, setRuntimePolicyHint] = useState<AssistantRuntimePolicyHint | null>(null);
  const pendingToolToolbarPreferencesRef = useRef<typeof DEFAULT_ASSISTANT_TOOLBAR_PREFERENCES | null>(null);
  const capabilityRequestKeyRef = useRef('');
  const toolRequestKeyRef = useRef('');
  const catalogScope = input.catalogScope ?? (
    input.effectivePersonaRole === 'parent' ? 'route' : 'staff-global'
  );
  const localAssistantCapabilitySeed = useMemo(
    () => resolveAssistantCatalogFixtureSeed({
      localFixture: input.localAssistantCapabilityFixture,
      useLocalCatalogFixture: input.useLocalCatalogFixture,
    }),
    [
      input.localAssistantCapabilityFixture,
      input.useLocalCatalogFixture,
    ],
  );
  const localAssistantToolSeed = useMemo(
    () => resolveAssistantCatalogFixtureSeed({
      localFixture: input.localAssistantToolFixture,
      useLocalCatalogFixture: input.useLocalCatalogFixture,
    }),
    [
      input.localAssistantToolFixture,
      input.useLocalCatalogFixture,
    ],
  );

  const rankedSnapshotEntries = useMemo(() => {
    const scenarioTab = resolveScenarioTabFromPath(input.pathname);
    const dataStateSignals = derivePromptDataStateSignals({
      pathname: input.pathname,
      context: input.context,
    });
    return listAssistantPromptMatrixSnapshotForContext({
      pathname: input.pathname,
      module: input.capabilityModule,
      persona: input.effectivePersonaRole,
      tab: scenarioTab,
      dataStateSignals,
      catalogScope,
    });
  }, [
    catalogScope,
    input.capabilityModule,
    input.context,
    input.effectivePersonaRole,
    input.pathname,
  ]);

  const rankedCapabilityIds = useMemo(
    () => new Map(rankedSnapshotEntries.map((entry, index) => [entry.id, index] as const)),
    [rankedSnapshotEntries],
  );
  const rankedSnapshotById = useMemo(
    () => new Map(rankedSnapshotEntries.map((entry) => [entry.id, entry] as const)),
    [rankedSnapshotEntries],
  );

  useEffect(() => {
    let cancelled = false;
    const requestKey = JSON.stringify({
      pathname: input.pathname,
      module: input.capabilityModule,
      persona: input.effectivePersonaRole,
      catalogScope,
    });
    capabilityRequestKeyRef.current = requestKey;
    setAssistantCapabilities(localAssistantCapabilitySeed);
    setAssistantCatalogLoadErrors((current) => ({
      ...current,
      capabilities: null,
    }));
    setAssistantCapabilitiesLoading(true);
    input.triggerListAssistantCapabilities('listAssistantCapabilities', {
      pathname: input.pathname,
      module: input.capabilityModule,
      persona: input.effectivePersonaRole,
      catalogScope,
      limit: catalogScope === 'staff-global' ? undefined : 100,
    })
      .then((payload) => {
        if (cancelled || capabilityRequestKeyRef.current !== requestKey) return;
        const nextCapabilities = filterAssistantCapabilitiesForScope(
          coerceAssistantCapabilities(payload),
          {
            pathname: input.pathname,
            capabilityModule: input.capabilityModule,
            catalogScope,
          },
        );
        const result = resolveAuthorizedAssistantCatalogSuccess(nextCapabilities);
        setAssistantCapabilities(result.items);
        setAssistantCatalogLoadErrors((current) => ({
          ...current,
          capabilities: result.error,
        }));
      })
      .catch((error) => {
        if (!cancelled && capabilityRequestKeyRef.current === requestKey) {
          const result = resolveAuthorizedAssistantCatalogFailure({
            localFixture: input.localAssistantCapabilityFixture,
            useLocalCatalogFixture: input.useLocalCatalogFixture,
            errorMessage: readDomainRouterErrorMessage(
              error,
              'Unable to load assistant capabilities from the authorized catalog.',
            ),
          });
          setAssistantCapabilities(result.items);
          setAssistantCatalogLoadErrors((current) => ({
            ...current,
            capabilities: result.error,
          }));
        }
      })
      .finally(() => {
        if (!cancelled && capabilityRequestKeyRef.current === requestKey) {
          setAssistantCapabilitiesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    catalogScope,
    input.capabilityModule,
    input.effectivePersonaRole,
    input.pathname,
    input.triggerListAssistantCapabilities,
    input.useLocalCatalogFixture,
    input.localAssistantCapabilityFixture,
    localAssistantCapabilitySeed,
  ]);

  useEffect(() => {
    const nextPreferences = parseAssistantToolToolbarPreferences(
      readPreferenceFilters(input.preferencesData)
    );
    if (!shouldApplyAssistantToolToolbarHydration(nextPreferences, pendingToolToolbarPreferencesRef.current)) {
      return;
    }
    pendingToolToolbarPreferencesRef.current = null;
    setToolToolbarPreferences((current) => (
      serializeAssistantToolToolbarPreferences(current) === serializeAssistantToolToolbarPreferences(nextPreferences)
        ? current
        : nextPreferences
    ));
  }, [input.preferencesData]);

  useEffect(() => {
    let cancelled = false;
    const requestKey = JSON.stringify({
      pathname: input.pathname,
      module: input.capabilityModule,
      persona: input.effectivePersonaRole,
      catalogScope,
    });
    toolRequestKeyRef.current = requestKey;
    setAssistantTools(localAssistantToolSeed);
    setAssistantCatalogLoadErrors((current) => ({
      ...current,
      tools: null,
    }));
    setAssistantToolsLoading(true);
    input.triggerListAssistantTools('listAssistantTools', {
      pathname: input.pathname,
      module: input.capabilityModule,
      persona: input.effectivePersonaRole,
      limit: catalogScope === 'staff-global' ? undefined : 24,
      catalogScope,
    })
      .then((payload) => {
        if (cancelled || toolRequestKeyRef.current !== requestKey) return;
        const nextTools = filterAssistantToolsForScope(
          coerceAssistantTools(payload),
          {
            pathname: input.pathname,
            capabilityModule: input.capabilityModule,
            effectivePersonaRole: input.effectivePersonaRole,
            catalogScope,
          },
        );
        const result = resolveAuthorizedAssistantCatalogSuccess(nextTools);
        setAssistantTools(result.items);
        setAssistantCatalogLoadErrors((current) => ({
          ...current,
          tools: result.error,
        }));
      })
      .catch((error) => {
        if (!cancelled && toolRequestKeyRef.current === requestKey) {
          const result = resolveAuthorizedAssistantCatalogFailure({
            localFixture: input.localAssistantToolFixture,
            useLocalCatalogFixture: input.useLocalCatalogFixture,
            errorMessage: readDomainRouterErrorMessage(
              error,
              'Unable to load assistant tools from the authorized catalog.',
            ),
          });
          setAssistantTools(result.items);
          setAssistantCatalogLoadErrors((current) => ({
            ...current,
            tools: result.error,
          }));
        }
      })
      .finally(() => {
        if (!cancelled && toolRequestKeyRef.current === requestKey) {
          setAssistantToolsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    catalogScope,
    input.capabilityModule,
    input.effectivePersonaRole,
    input.pathname,
    input.triggerListAssistantTools,
    input.useLocalCatalogFixture,
    input.localAssistantToolFixture,
    localAssistantToolSeed,
  ]);

  useEffect(() => {
    let cancelled = false;
    input.triggerGetAssistantRuntimePolicy('getAssistantRuntimePolicy', {
      pathname: input.pathname,
      module: input.capabilityModule,
      persona: input.effectivePersonaRole,
    })
      .then((payload) => {
        if (!cancelled) {
          setRuntimePolicyHint(coerceAssistantRuntimePolicyHint(payload));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRuntimePolicyHint(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    input.capabilityModule,
    input.effectivePersonaRole,
    input.pathname,
    input.triggerGetAssistantRuntimePolicy,
  ]);

  const routeScopedAssistantCapabilities = useMemo(
    () => filterAssistantCapabilitiesForScope(assistantCapabilities, {
      pathname: input.pathname,
      capabilityModule: input.capabilityModule,
      catalogScope,
    }),
    [
      assistantCapabilities,
      catalogScope,
      input.capabilityModule,
      input.pathname,
    ],
  );
  const routeScopedAssistantTools = useMemo(
    () => filterAssistantToolsForScope(assistantTools, {
      pathname: input.pathname,
      capabilityModule: input.capabilityModule,
      effectivePersonaRole: input.effectivePersonaRole,
      catalogScope,
    }),
    [
      assistantTools,
      catalogScope,
      input.capabilityModule,
      input.effectivePersonaRole,
      input.pathname,
    ],
  );

  const resolvedAssistantCapabilities = routeScopedAssistantCapabilities;
  const resolvedAssistantTools = routeScopedAssistantTools;

  const persistToolToolbarPreferences = useCallback(async (
    nextPreferences: typeof toolToolbarPreferences,
    failureMessage: string,
  ) => {
    const previousPreferences = toolToolbarPreferences;
    pendingToolToolbarPreferencesRef.current = nextPreferences;
    setToolToolbarPreferences(nextPreferences);
    try {
      const currentFilters = readPreferenceFilters(input.preferencesData);
      const nextFilters = writeAssistantToolToolbarPreferences(currentFilters, nextPreferences);
      await input.triggerUpdateUserPreferences({
        input: JSON.stringify({ filters: nextFilters }),
      });
    } catch {
      pendingToolToolbarPreferencesRef.current = null;
      setToolToolbarPreferences(previousPreferences);
      input.onSetLastActionFeedback(failureMessage);
    }
  }, [input, toolToolbarPreferences]);

  const toggleToolToolbarCollapsed = useCallback(() => {
    void persistToolToolbarPreferences({
      ...toolToolbarPreferences,
      collapsed: !toolToolbarPreferences.collapsed,
      hiddenAll: false,
    }, 'Unable to update assistant tools toolbar preference right now.');
  }, [persistToolToolbarPreferences, toolToolbarPreferences]);

  const hideToolFromToolbar = useCallback((toolId: string) => {
    const hiddenToolIds = Array.from(new Set([...toolToolbarPreferences.hiddenToolIds, toolId])).sort();
    void persistToolToolbarPreferences({
      ...toolToolbarPreferences,
      hiddenAll: hiddenToolIds.length >= resolvedAssistantTools.filter((tool) => tool.userVisible && tool.visibility === 'visible').length,
      hiddenToolIds,
    }, 'Unable to hide this tool right now.');
  }, [persistToolToolbarPreferences, resolvedAssistantTools, toolToolbarPreferences]);

  const hideAllToolsFromToolbar = useCallback(() => {
    void persistToolToolbarPreferences({
      ...toolToolbarPreferences,
      hiddenAll: true,
      collapsed: true,
    }, 'Unable to hide assistant tools right now.');
  }, [persistToolToolbarPreferences, toolToolbarPreferences]);

  const showAllToolsInToolbar = useCallback(() => {
    void persistToolToolbarPreferences({
      ...toolToolbarPreferences,
      hiddenAll: false,
      hiddenToolIds: [],
    }, 'Unable to restore assistant tools right now.');
  }, [persistToolToolbarPreferences, toolToolbarPreferences]);

  const orderedCapabilities = useMemo(() => (
    resolvedAssistantCapabilities
      .slice()
      .sort((left, right) => {
        const leftRank = rankedCapabilityIds.get(left.id) ?? Number.MAX_SAFE_INTEGER;
        const rightRank = rankedCapabilityIds.get(right.id) ?? Number.MAX_SAFE_INTEGER;
        return (
          leftRank - rightRank
          || left.sequence - right.sequence
          || left.title.localeCompare(right.title)
        );
      })
  ), [rankedCapabilityIds, resolvedAssistantCapabilities]);

  const capabilityPrompts = useMemo((): AssistantPromptUi[] => {
    if (orderedCapabilities.length === 0) {
      return [];
    }
    return orderedCapabilities.slice(0, 12).map((capability) => {
      const snapshotEntry = rankedSnapshotById.get(capability.id);
      const likelySources = capability.likelySources?.length
        ? capability.likelySources
        : (snapshotEntry?.likelySources ?? []);
      const routePrefixes = capability.routePrefixes?.length
        ? capability.routePrefixes
        : (snapshotEntry?.routePrefixes ?? []);
      return {
        id: `capability-${capability.id}`,
        label: capability.title,
        badge: capability.type === 'action' ? 'Action' : undefined,
        provenance: {
          capabilityId: capability.id,
          sourceOrigin: capability.sourceOrigin ?? snapshotEntry?.sourceOrigin ?? null,
          scenarioKey: capability.scenarioKey ?? snapshotEntry?.scenarioKey ?? null,
          method: snapshotEntry?.method ?? capability.method ?? null,
          likelySources,
          routePrefixes,
        },
      };
    });
  }, [orderedCapabilities, rankedSnapshotById]);

  const capabilityByPromptId = useMemo<Map<string, AssistantCapability>>(() => (
    new Map<string, AssistantCapability>(
      orderedCapabilities.map((capability) => [
        `capability-${capability.id}`,
        capability,
      ] as const)
    )
  ), [orderedCapabilities]);

  const capabilityById = useMemo(
    () => new Map(orderedCapabilities.map((capability) => [capability.id, capability] as const)),
    [orderedCapabilities],
  );

  const filteredCatalogCapabilities = useMemo(() => {
    const query = normalizeAssistantText(capabilityCatalogQuery);
    if (!query) {
      return orderedCapabilities;
    }
    return orderedCapabilities.filter((capability) => (
      normalizeAssistantText([
        capability.title,
        capability.prompt,
        capability.rationale,
        ASSISTANT_MODULE_LABELS[capability.module],
      ].join(' ')).includes(query)
    ));
  }, [capabilityCatalogQuery, orderedCapabilities]);

  const filteredCatalogTools = useMemo(() => {
    const visibleTools = resolvedAssistantTools
      .filter((tool) => tool.userVisible && tool.visibility === 'visible')
      .filter((tool) => !tool.capabilityId);
    const query = normalizeAssistantText(capabilityCatalogQuery);
    if (!query) {
      return visibleTools;
    }
    return visibleTools.filter((tool) => (
      normalizeAssistantText([
        tool.title,
        tool.summary,
        tool.prompt,
        tool.workflowKey,
        ASSISTANT_MODULE_LABELS[tool.module],
      ].join(' ')).includes(query)
    ));
  }, [capabilityCatalogQuery, resolvedAssistantTools]);

  const filteredSettingsSpecialistCases = useMemo(() => {
    const cases = input.settingsSpecialistSnapshot?.presetCases ?? [];
    const query = normalizeAssistantText(capabilityCatalogQuery);
    if (!query) {
      return cases;
    }
    return cases.filter((entry) => normalizeAssistantText([
      entry.label,
      entry.summary,
      entry.prompt,
      entry.badge ?? '',
    ].join(' ')).includes(query));
  }, [capabilityCatalogQuery, input.settingsSpecialistSnapshot]);

  const visibleSettingsSpecialistCases = useMemo(() => {
    if (!input.settingsSpecialistSnapshot) {
      return [];
    }
    const query = normalizeAssistantText(capabilityCatalogQuery);
    if (input.showAmbientSettingsSpecialist || query.length > 0) {
      return filteredSettingsSpecialistCases;
    }
    return [];
  }, [
    capabilityCatalogQuery,
    filteredSettingsSpecialistCases,
    input.settingsSpecialistSnapshot,
    input.showAmbientSettingsSpecialist,
  ]);

  const capabilityCatalogSections = useMemo(() => {
    const sections = new Map<string, AssistantCapability[]>();
    for (const capability of filteredCatalogCapabilities) {
      const label = ASSISTANT_MODULE_LABELS[capability.module];
      const existing = sections.get(label) ?? [];
      existing.push(capability);
      sections.set(label, existing);
    }
    return Array.from(sections.entries()).map(([label, capabilities]) => ({ label, capabilities }));
  }, [filteredCatalogCapabilities]);

  const toolCatalogSections = useMemo(() => {
    const sections = new Map<string, AssistantTool[]>();
    for (const tool of filteredCatalogTools) {
      const label = ASSISTANT_MODULE_LABELS[tool.module];
      const existing = sections.get(label) ?? [];
      existing.push(tool);
      sections.set(label, existing);
    }
    return Array.from(sections.entries()).map(([label, tools]) => ({ label, tools }));
  }, [filteredCatalogTools]);

  const settingsSpecialistPromptSections = useMemo(() => (
    visibleSettingsSpecialistCases.length > 0
      ? [{
        label: input.settingsSpecialistSnapshot?.browserSectionLabel ?? 'Settings specialist reviews',
        prompts: visibleSettingsSpecialistCases.map((entry) => ({
          id: entry.id,
          label: entry.label,
          summary: entry.summary,
          badge: entry.badge,
        })),
      }]
      : []
  ), [input.settingsSpecialistSnapshot, visibleSettingsSpecialistCases]);

  const capabilityCatalogTotalCount =
    filteredCatalogCapabilities.length +
    filteredCatalogTools.length +
    visibleSettingsSpecialistCases.length;
  const hiddenToolIdSet = useMemo(
    () => new Set(toolToolbarPreferences.hiddenToolIds),
    [toolToolbarPreferences.hiddenToolIds],
  );
  const toolbarTools = useMemo(
    () => resolvedAssistantTools
      .filter((tool) => tool.userVisible && tool.visibility === 'visible')
      .filter((tool) => !hiddenToolIdSet.has(tool.toolId))
      .slice(0, 8),
    [hiddenToolIdSet, resolvedAssistantTools],
  );

  useEffect(() => {
    if (!showCapabilityCatalog) {
      return;
    }
    setCapabilityBrowserPreviewById({});
  }, [input.capabilityModule, input.effectivePersonaRole, input.pathname, showCapabilityCatalog]);

  const actionContextPayload = useMemo(
    () => sanitizeAwsJsonObject(input.context),
    [input.context],
  );

  const loadCapabilityBrowserPreview = useCallback(async (
    capability: AssistantCapability,
  ): Promise<AssistantActionPreviewResponse | null> => {
    if (capability.type !== 'action') {
      return null;
    }
    setCapabilityBrowserPreviewById((current) => ({
      ...current,
      [capability.id]: {
        status: 'loading',
        preview: current[capability.id]?.preview ?? null,
        errorMessage: null,
      },
    }));
    try {
      const previewPayload = await input.triggerPreviewAssistantAction('previewAssistantAction', {
        capabilityId: capability.id,
        pathname: resolveAssistantCatalogTargetPathname({
          targetRoute: capability.targetRoute,
          routePrefixes: capability.routePrefixes,
          fallbackPathname: input.pathname,
        }),
        module: capability.module,
        persona: input.effectivePersonaRole,
        context: actionContextPayload,
      });
      const preview = coerceAssistantActionPreview(previewPayload);
      setCapabilityBrowserPreviewById((current) => ({
        ...current,
        [capability.id]: {
          status: 'ready',
          preview,
          errorMessage: null,
        },
      }));
      return preview;
    } catch (error) {
      const errorMessage = readDomainRouterErrorMessage(error, 'Unable to verify this action right now.');
      setCapabilityBrowserPreviewById((current) => ({
        ...current,
        [capability.id]: {
          status: 'error',
          preview: null,
          errorMessage,
        },
      }));
      return null;
    }
  }, [
    actionContextPayload,
    input.effectivePersonaRole,
    input.pathname,
    input.triggerPreviewAssistantAction,
  ]);

  useEffect(() => {
    if (!showCapabilityCatalog) {
      return;
    }
    const candidates = filteredCatalogCapabilities.filter((capability) => capability.type === 'action');
    for (const capability of candidates) {
      const previewState = capabilityBrowserPreviewById[capability.id];
      if (previewState?.status === 'ready' || previewState?.status === 'loading') {
        continue;
      }
      void loadCapabilityBrowserPreview(capability);
    }
  }, [
    capabilityBrowserPreviewById,
    filteredCatalogCapabilities,
    loadCapabilityBrowserPreview,
    showCapabilityCatalog,
  ]);

  return {
    actionContextPayload,
    assistantCatalogLoadErrors,
    assistantCapabilitiesLoading,
    assistantToolsLoading,
    capabilityBrowserPreviewById,
    capabilityById,
    capabilityByPromptId,
    capabilityCatalogQuery,
    capabilityCatalogSections,
    capabilityCatalogTotalCount,
    toolCatalogSections,
    capabilityPrompts,
    filteredSettingsSpecialistCases,
    loadCapabilityBrowserPreview,
    orderedCapabilities,
    rankedSnapshotById,
    resolvedAssistantCapabilities,
    resolvedAssistantTools,
    runtimePolicyHint,
    setCapabilityCatalogQuery,
    setShowCapabilityCatalog,
    settingsSpecialistPromptSections,
    showCapabilityCatalog,
    toggleToolToolbarCollapsed,
    toolbarTools,
    toolToolbarPreferences,
    hideToolFromToolbar,
    hideAllToolsFromToolbar,
    showAllToolsInToolbar,
    visibleSettingsSpecialistCases,
  };
};
